import { combineReducers } from 'redux';

import demoKnowledgeBaseReducer from 'reducers/knowledgeBase';
import appReducer from 'reducers/app';
import configurationReducer from 'reducers/configuration';
import messagesReducer from 'reducers/messages';
import uiReducer from 'reducers/ui';
import userReducer from 'reducers/user';
import sessionReducer from 'reducers/session';
import selfServiceReducer from 'reducers/selfService';

import appSelectors from 'selectors/app';
import configSelectors from 'selectors/configuration';
import messageSelectors from 'selectors/messages';
import sessionSelectors from 'selectors/session';
import uiSelectors from 'selectors/ui';
import userSelectors from 'selectors/user';
import selfServiceSelectors from 'selectors/selfService';
import kbSelectors from 'selectors/knowledgeBase';

import appInitialState from 'reducers/initialStates/app';
import configurationInitialState from 'reducers/initialStates/configuration';
import kbInitialState from 'reducers/initialStates/knowledgeBase';
import messagesInitialState from 'reducers/initialStates/messages';
import sessionInitialState from 'reducers/initialStates/session';
import uiInitialState from 'reducers/initialStates/ui';
import userInitialState from 'reducers/initialStates/user';
import selfServiceInitialState from 'reducers/initialStates/selfService';

import configEnums from 'enums/configuration';

// Root reducer
export default combineReducers({
  answers: demoKnowledgeBaseReducer,
  app: appReducer,
  configuration: configurationReducer,
  messages: messagesReducer,
  ui: uiReducer,
  user: userReducer,
  selfService: selfServiceReducer,
  session: sessionReducer,
});

// Initial state

export const initialState = {
  app: appInitialState,
  answers: kbInitialState,
  configuration: configurationInitialState,
  messages: messagesInitialState,
  session: sessionInitialState,
  selfService: selfServiceInitialState,
  ui: uiInitialState,
  user: userInitialState,
};

// Configuration selectors
export const isSmoochEnabled = state => {
  return configSelectors.isSmoochEnabled(state.configuration);
};

export const isSelfServiceEnabled = state => {
  const widgetType = getWidgetType(state);
  const widgetTypes = configEnums.WIDGET_TYPES;
  return widgetType === widgetTypes.SELF_SERVICE || widgetType === widgetTypes.DEMO_SELF_SERVICE;
};

export const getWithinOfficeHours = state => {
  return configSelectors.getWithinOfficeHours(state.configuration);
};

export const getAlignment = state => {
  return configSelectors.getAlignment(state.configuration);
};

export const getBorder = state => {
  return configSelectors.getBorder(state.configuration);
};

export const getBorderRadius = state => {
  return configSelectors.getBorderRadius(state.configuration);
};

export const getChatClosed = state => {
  return configSelectors.getChatClosed(state.configuration);
};

export const getChatThrottled = state => {
  return configSelectors.getChatThrottled(state.configuration);
};

export const getCloseButton = state => {
  return configSelectors.getCloseButton(state.configuration);
};

export const getMinimizedChatButton = state => {
  return configSelectors.getMinimizedChatButton(state.configuration);
};

export const getWelcomeMessage = state => {
  let sessionWelcomeMessage = sessionSelectors.getSessionWelcomeMessage(state.session);
  let configWelcomeMessage = configSelectors.getOngoingChat(state.configuration).welcomeMessage;
  return sessionWelcomeMessage || configWelcomeMessage;
};

export const hasActiveSession = state => {
  return sessionSelectors.hasActiveSession(state.session);
};

export const getColors = state => {
  return configSelectors.getColors(state.configuration);
};

export const getInitialChatButton = state => {
  return configSelectors.getInitialChatButton(state.configuration);
};

export const getIsDraggable = state => {
  return configSelectors.getIsDraggable(state.configuration);
};

export const isDemoSelfServiceEnabled = state => {
  return configSelectors.isDemoSelfServiceEnabled(state.configuration);
};

export const getOnboarding = state => {
  return configSelectors.getOnboarding(state.configuration);
};

export const getOngoingChat = state => {
  return configSelectors.getOngoingChat(state.configuration);
};

export const getSelfService = state => {
  return configSelectors.getSelfService(state.configuration);
};

export const getDropshadowEnabled = state => {
  return configSelectors.getDropshadowEnabled(state.configuration);
};

export const getFont = state => {
  return configSelectors.getFont(state.configuration);
};

export const getWidgetType = state => {
  return configSelectors.getWidgetType(state.configuration);
};

export const getZIndex = state => {
  return configSelectors.getZIndex(state.configuration);
};

export const getCustomText = state => {
  return configSelectors.getCustomText(state.configuration);
};

// Knowledge Base selectors
export const getAnswers = state => {
  return kbSelectors.getAnswers(state.answers);
};

export const getSuggestedAnswers = state => {
  return kbSelectors.getSuggestedAnswers(state.answers);
};

export const getKBSearchResults = state => {
  return kbSelectors.getKBSearchResults(state.answers);
};

export const getAnswerById = (state, id) => {
  return kbSelectors.getAnswerById(state.answers, id);
};

export const getSelectedAnswer = state => {
  return kbSelectors.getSelectedAnswer(state.answers);
};

// Self Service selectors

export const getIsNavigatingBack = state => {
  return selfServiceSelectors.getIsNavigatingBack(state.selfService);
};

export const getIsThrottled = state => {
  return selfServiceSelectors.getIsThrottled(state.selfService);
};

export const getResultsScrollTop = state => {
  return selfServiceSelectors.getResultsScrollTop(state.selfService);
};

export const getSelfServiceSearch = state => {
  return selfServiceSelectors.getSelfServiceSearch(state.selfService);
};

export const getSelfServiceSearchTerm = state => {
  return selfServiceSelectors.getSearchTerm(state.selfService);
};

export const getSelectedSelfServiceAnswer = state => {
  return selfServiceSelectors.getSelectedAnswer(state.selfService);
};

// Message selectors

export const getMessages = state => {
  const userId = getUserId(state);
  const messages = messageSelectors.getMessages(state.messages);

  if (isSelfServiceEnabled(state) && messages.length > 0) {
    const body = getCustomText(state).firstMessageAutoResponse;
    const firstMessageDate = messages[0] && messages[0].date;
    const autoResponse = {
      id: 'auto-response',
      date: firstMessageDate,
      isUser: false,
      content: { body, type: 'text' },
    };
    messages.splice(1, 0, autoResponse);
  }

  if (isSmoochEnabled(state)) {
    return messages;
  }

  return messages.map(message => ({
    ...message,
    isUser: message.participantId === userId,
  }));
};

export const getUnreadAgentMessages = state => {
  const userId = getUserId(state);
  let dateMinimized = uiSelectors.getminimizedAt(state.ui);
  const messagesAfterDate = messageSelectors.getMessagesAfterDate(state.messages, dateMinimized);

  if (isSmoochEnabled(state)) {
    return messagesAfterDate.filter(message => !message.isUser);
  }
  return messagesAfterDate.filter(message => message.participantId !== userId);
};

export const getMessageById = (state, id) => {
  return messageSelectors.getMessageById(state.messages, id);
};

// User selectors

export const getIsExistingUser = state => {
  return !!userSelectors.getId(state.user);
};

export const getUserId = state => {
  return userSelectors.getUserId(state.user);
};

export const getUserGladlyCustomerId = state => {
  return userSelectors.getGladlyCustomerId(state.user);
};

export const getUserName = state => {
  return userSelectors.getName(state.user);
};

export const getUserEmail = state => {
  return userSelectors.getEmail(state.user);
};

// UI selectors

export const getIsVisible = state => {
  return uiSelectors.getIsVisible(state.ui);
};

export const getIsMinimized = state => {
  return uiSelectors.getIsMinimized(state.ui);
};

export const getStage = state => {
  return uiSelectors.getStage(state.ui);
};

export const getCompositionDraft = state => {
  return uiSelectors.getCompositionDraft(state.ui);
};

export const getCurrentLocation = state => {
  return uiSelectors.getCurrentLocation(state.ui);
};

export const getUnreadMessageCount = state => {
  return uiSelectors.getUnreadMessageCount(state.ui);
};

// APP selectors

export const getAuthError = state => {
  return appSelectors.getAuthError(state.app);
};

export const getProactiveTriggered = state => {
  return appSelectors.getProactiveTriggered(state.app);
};

export const isAppInitialized = state => {
  return appSelectors.isAppInitialized(state.app);
};

export const isClientReady = state => {
  return appSelectors.isClientReady(state.app);
};

export const getChatClientConnectionStatus = state => {
  return appSelectors.getChatClientConnectionStatus(state.app);
};
